"use client";

import { motion, useMotionValue, useTransform } from "framer-motion";
import { MouseEvent } from "react";
import Magnetic from "./ui/Magnetic";

const timeline = [
  {
    year: "2026",
    title: "Digital Specialist Engineer",
    company: "Infosys",
    description: "Orchestrating microservices, automating server infrastructure deployments, and structuring secure high-performance data systems.",
  },
  {
    year: "2025",
    title: "Advanced DevOps focus",
    company: "Open Source / Independent",
    description: "Building automated CI/CD pipeline triggers, container routing via Traefik/NGINX proxies, and container setups using Docker Compose.",
  },
  {
    year: "2024",
    title: "SaaS Product Engineering",
    company: "SaaS Builders",
    description: "Constructing multi-tenant database designs, JWT cookies authentication structures, and sub-millisecond query caches.",
  },
  {
    year: "2023",
    title: "First Large Scale Builds",
    company: "Contract / Government Systems",
    description: "Architecting StreamVision CCTV transcoders for state municipal boards, handling video segment caching under concurrent loads.",
  },
  {
    year: "2022",
    title: "Web Engineering Ingress",
    company: "Self-Initiated Dev Node",
    description: "Immersing in full stack JavaScript/TypeScript architectures, mastering relational schemas design and web compliance rules.",
  }
];

function Card({
  children,
  className,
  span,
}: {
  children: React.ReactNode;
  className?: string;
  span?: string;
}) {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  function handleMouseMove({ currentTarget, clientX, clientY }: MouseEvent) {
    const { left, top } = currentTarget.getBoundingClientRect();
    mouseX.set(clientX - left);
    mouseY.set(clientY - top);
  }

  return (
    <motion.div
      onMouseMove={handleMouseMove}
      className={`group relative overflow-hidden rounded-[2rem] border border-white/5 bg-gradient-to-br from-white/[0.03] to-transparent p-8 md:p-10 ${span} ${className}`}
    >
      <motion.div
        className="pointer-events-none absolute -inset-px rounded-[2rem] opacity-0 transition duration-300 group-hover:opacity-100"
        style={{
          background: useTransform(
            [mouseX, mouseY],
            ([x, y]) =>
              `radial-gradient(400px circle at ${x}px ${y}px, rgba(99, 102, 241, 0.08), transparent 85%)`
          ),
        }}
      />
      {children}
    </motion.div>
  );
}

export default function About() {
  return (
    <section id="about" className="section-pad relative z-10 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Biography Story Column */}
          <Card span="lg:col-span-7 flex flex-col justify-center">
            <span className="text-xs font-bold tracking-[0.3em] text-indigo-400 uppercase mb-6 block font-mono">
              Identity Node
            </span>
            <h2 className="text-4xl md:text-5xl lg:text-7xl font-black text-white mb-8 font-[var(--font-poppins)] tracking-tighter leading-none">
              The Mind <span className="gradient-text">Behind</span> The Machines
            </h2>
            
            <div className="space-y-6 text-gray-300 font-medium text-sm md:text-base leading-relaxed">
              <p>
                I am <span className="text-white font-bold">Amrit Sharma</span>, a System Architect, DevOps Engineer, and Full Stack builder focused on creating highly optimized digital products that scale. I bridge the gap between complex software designs and seamless, secure production deployments.
              </p>
              <p>
                <span className="text-indigo-300 font-bold block mb-1">Why I build software:</span>
                I believe software should run reliably under any load. I am motivated by the challenge of resolving high-concurrency data flows, structuring clean APIs schemas, and reducing page delivery times down to the absolute limit.
              </p>
              <p>
                <span className="text-indigo-300 font-bold block mb-1">How I solve problems:</span>
                My approach is analytical and data-driven. Before coding, I map database relational networks, test latency profiles, and structure deployment gates to catch issues early. When bugs occur, I trace execution threads and examine heap allocations.
              </p>
              <p>
                <span className="text-indigo-300 font-bold block mb-1">How I communicate & build teams:</span>
                I prefer transparent updates, detailed documentation (READMEs and architectural plans), and strict coding guidelines. I enjoy working in teams that prioritize technical quality, peer reviews, and automated deployment processes over manual effort.
              </p>
            </div>

            <div className="h-px bg-white/5 my-8" />

            <div className="flex flex-wrap gap-4">
              <a
                href="mailto:contact@amritsharma.dev?subject=Project%20Inquiry"
                className="flex items-center gap-3 group/link"
              >
                <Magnetic>
                  <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center transition-colors group-hover/link:bg-indigo-500/20 text-indigo-400">
                    <i className="fi fi-rr-envelope" />
                  </div>
                </Magnetic>
                <span className="text-xs font-bold text-gray-400 group-hover/link:text-white transition-colors uppercase tracking-widest font-mono">
                  contact@amritsharma.dev
                </span>
              </a>
              
              <a
                href="/profile.html"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 group/link"
              >
                <Magnetic>
                  <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center transition-colors group-hover/link:bg-white/10 text-white">
                    <i className="fi fi-rr-document" />
                  </div>
                </Magnetic>
                <span className="text-xs font-bold text-gray-400 group-hover/link:text-white transition-colors uppercase tracking-widest font-mono">
                  Download Dossier
                </span>
              </a>
            </div>
          </Card>

          {/* Timeline Column */}
          <Card span="lg:col-span-5">
            <span className="text-xs font-bold tracking-[0.3em] text-indigo-400 uppercase mb-6 block font-mono">
              Development Nodes Trace
            </span>
            <h3 className="text-2xl font-black text-white mb-8 uppercase tracking-wider font-[var(--font-poppins)]">
              Timeline Milestones
            </h3>
            
            <div className="relative border-l border-white/10 ml-4 pl-8 space-y-8">
              {timeline.map((item, idx) => (
                <div key={idx} className="relative">
                  {/* Glowing Node Dot */}
                  <span className="absolute -left-[2.85rem] top-1 w-4 h-4 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 border-[3px] border-[#020306] shadow-[0_0_10px_rgba(99,102,241,0.5)]" />
                  
                  <span className="text-[10px] font-bold text-indigo-400 font-mono tracking-widest block mb-1">
                    {item.year} • {item.company}
                  </span>
                  
                  <h4 className="text-sm font-black text-white uppercase tracking-wider mb-2">
                    {item.title}
                  </h4>
                  
                  <p className="text-xs text-gray-500 leading-relaxed font-medium">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
}
